import { useCallback, useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell } from "recharts";
import styles from "./style.module.scss";

import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { IContractStats } from "../../../../setup/api/services/contract/models";
import { getContractStats } from "../../../../setup/api/services/contract";

const COLORS = ["#0D44AD", "#EBF4FF"];

interface Props {
  milestoneId: string | undefined;
}

const StatsDetails = ({ milestoneId }: Props): JSX.Element => {
  const { t } = useTranslation();
  const [contractStats, setContractStats] = useState<IContractStats | null>(
    null
  );

  const fetchContractStats = useCallback(async () => {
    try {
      const stats = await getContractStats(milestoneId || "");
      setContractStats(stats.data);
    } catch (error) {
      console.log(error);
    }
  }, [milestoneId]);

  useEffect(() => {
    if (milestoneId) {
      fetchContractStats();
    }
  }, [milestoneId, fetchContractStats]);

  const clientData = useMemo(() => {
    return [
      {
        name: "Unregistered",
        value: contractStats?.activeUsersStats.unRegistered || 0,
      },
      {
        name: "Active",
        value: contractStats?.activeUsersStats.registered || 0,
      },
    ];
  }, [contractStats]);

  const paymentData = useMemo(() => {
    return [
      { name: "Paid", value: contractStats?.paidUsersStats.paid },
      { name: "Pending", value: contractStats?.paidUsersStats.unPaid },
    ];
  }, [contractStats]);

  if (!contractStats) {
    return <div></div>;
  }

  return (
    <div className={styles.scrollContainer}>
      {/* Static Transaction Summary */}
      <div className={styles.card}>
        <div className={styles.header}>
          <p className={classNames(styles.subtitle4, styles.semi_bold)}>
            {t("lbl_payment_title")}
          </p>
          <p className={styles.amount}>{`${t("total")} : ${(
            contractStats.paymentStats.amountPaid +
            contractStats.paymentStats.amountUnpaid
          ).toLocaleString()} ${t("lbl_saudi_currency")}`}</p>
        </div>

        <div className={styles.barWrapper}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${
                  (contractStats.paymentStats.amountPaid /
                    (contractStats.paymentStats.amountPaid +
                      contractStats.paymentStats.amountUnpaid)) *
                  100
                }%`,
              }}
            />
          </div>
        </div>

        <div className={styles.amounts}>
          <div className={styles.amount}>
            <span
              className={styles.dotDark}
              style={{ backgroundColor: "#3ECC85" }}
            />
            <div>
              <span>{t("paid")}</span>
              <p>
                {contractStats.paymentStats.amountPaid.toLocaleString() +
                  " " +
                  t("lbl_saudi_currency")}
              </p>
            </div>
          </div>
          <div className={styles.amount}>
            <span
              className={styles.dotLight}
              style={{ backgroundColor: "#EEF0F5" }}
            />
            <div>
              <span>{t("remaining")}</span>
              <p>
                {contractStats.paymentStats.amountUnpaid.toLocaleString() +
                  " " +
                  t("lbl_saudi_currency")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Static Payment Donut */}
      <div className={styles.doughnutCard}>
        <div className={styles.chartLegend}>
          <div className={styles.chartText}>
            <span
              className={styles.dotDark}
              style={{ backgroundColor: "#0D44AD" }}
            />
            <div className={styles.text}>
              <span>{t("paid_client")}</span>
              <p>{contractStats?.paidUsersStats.paid}</p>
            </div>
          </div>
          <div className={styles.chartText}>
            <span
              className={styles.dotLight}
              style={{ backgroundColor: "#EBF4FF" }}
            />
            <div className={styles.text}>
              <span>{t("waiting_for_payment")}</span>
              <p>{contractStats?.paidUsersStats.unPaid}</p>
            </div>
          </div>
        </div>
        <div className={styles.donutWrapper}>
          <PieChart width={98} height={98}>
            <Pie
              data={paymentData}
              innerRadius={32}
              outerRadius={48}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none">
              <Cell fill={COLORS[0]} />
              <Cell fill={COLORS[1]} />
            </Pie>
          </PieChart>
          <div className={styles.chartLabel}>
            <p className="subtitle4">
              {contractStats?.paidUsersStats.paid +
                contractStats?.paidUsersStats.unPaid}
            </p>
          </div>
        </div>
      </div>

      {/* Client Donut */}
      <div className={styles.doughnutCard}>
        <div className={styles.chartLegend}>
          <div className={styles.chartText}>
            <span
              className={styles.dotDark}
              style={{ backgroundColor: "#F3F6FC" }}
            />
            <div className={styles.text}>
              <span>{t("not_registered")}</span>
              <p>{contractStats?.activeUsersStats.unRegistered}</p>
            </div>
          </div>
          <div className={styles.chartText}>
            <span
              className={styles.dotLight}
              style={{ backgroundColor: "#639FFB" }}
            />
            <div className={styles.text}>
              <span>{t("active_client")}</span>
              <p>{contractStats?.activeUsersStats.registered}</p>
            </div>
          </div>
        </div>
        <div className={styles.donutWrapper}>
          <PieChart width={98} height={98}>
            <Pie
              data={clientData}
              innerRadius={32}
              outerRadius={48}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none">
              <Cell fill={"#F3F6FC"} />
              <Cell fill={"#639FFB"} />
            </Pie>
          </PieChart>
          <div className={styles.chartLabel}>
            <p className="subtitle4">
              {contractStats?.activeUsersStats.registered +
                contractStats?.activeUsersStats.unRegistered}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsDetails;
